<template>
  <aside class="questions-overview">
    <ul class="questions-overview-list">
      <li
        v-for="(question, index) in questions"
        :key="question.Question.id"
        class="questions-overview-list-item"
        :class="{
          answered: isQuestionAnswered(question),
          validated: isQuestionValidated(question),
          correct: isQuestionAnswerCorrect(question),
          current: index === currentIndex,
        }"
        :role="lockNavigation ? '' : 'button'"
        @click="onItemClick(index)"
      >
        {{ index + 1 }}
      </li>
    </ul>
  </aside>
</template>

<script>
/**
 * Displays a grid of question indexes with color codes representing unanswered, answered, wrong and correct answers.
 * Also highlights the current question index if given.
 * When a question index is clicked, a `navigate-to-index` event is emitted with the clicked index.
 *
 * Please note: The rendered index is 1 higher than the actual index, to make the array indexes human friendly.
 */
export default {
  name: 'QuestionsOverview',
  props: {
    currentIndex: { type: Number, default: -1 },
    lockNavigation: { type: Boolean, default: false },
    questions: {
      type: Array,
      required: true,
    },
  },
  methods: {
    isQuestionAnswered(question) {
      return question.UserAnswer !== undefined
    },
    isQuestionValidated(question) {
      return question.UserAnswer && question.UserAnswer.correct !== undefined
    },
    isQuestionAnswerCorrect(question) {
      return question.UserAnswer && question.UserAnswer.correct
    },
    onItemClick(index) {
      if (!this.lockNavigation) {
        this.$emit('navigate-to-index', index)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.questions-overview {
  position: relative;
  background: $frameBackgroundColor;
}

.questions-overview-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(2em, 1fr));
  justify-items: center;
  margin: 0;
  padding: 1em;
  grid-gap: 1em;
}

.questions-overview-list-item {
  $size: 1.8em;
  --background-color: #{$grayLighter};

  height: $size;
  width: $size;
  line-height: $size + 0.05em;
  padding: 0;
  margin: 0;
  text-align: center;
  background: var(--background-color);
  border: 0.2em solid var(--background-color);
  border-radius: 100%;
  box-shadow: 0 0.1em 0.1em rgba(0, 0, 0, 0.3);
  outline: 0;
  transition: all 0.15s ease-out;

  &.current {
    border-color: $blueVeryDark;
  }

  &.answered {
    color: $white;
    --background-color: #{$blueVeryDark};

    &.validated {
      --background-color: #{saturate(adjust-hue($green, -120deg), 20%)};

      &.correct {
        --background-color: #{$green};
      }
    }

    &:not(.validated):not(.current) {
      border-color: $grayLighter;
    }
  }

  &:active {
    transform: scale(0.95);
  }

  @media (hover: hover) {
    &[role='button']:hover {
      transform: scale(1.15);
    }
  }
}
</style>
